<template>
  <div class="notification-listener">
    <!-- This component doesn't render anything, it just listens for events -->
    <ParcelViewModal
      v-model="showParcelModal"
      :parcel="selectedParcel"
      editable
    />
  </div>
</template>

<script setup>
import { inject, onBeforeUnmount, onMounted, ref } from 'vue'
import { useToast } from 'vue-toastification/dist/index.mjs'
import { useNotifications } from '@/composables/useNotifications'
import ParcelViewModal from '@/components/shipment/ParcelViewModal.vue'
import axios from 'axios'
import { useRoute, useRouter } from 'vue-router'

const route = useRoute()
const router = useRouter()
const toast = useToast()

// Unified composable usage
const { addNotification, fetchNotifications, markAllRead } = useNotifications()

// Auth
const authStore = inject('authStore', null)
const userId = ref(authStore?.userData?.id || null)

// Get user team data from multiple possible sources
const getUserTeamData = () => {
  // Try different possible sources for team data
  let teamData = null
  
  if (authStore?.userData) {
    teamData = authStore.userData.team || 
               authStore.userData.Team || 
               null
  }
  
  // Also check localStorage as backup
  if (!teamData) {
    try {
      const fullUserData = JSON.parse(localStorage.getItem('fullUserData') || '{}')
      teamData = fullUserData.team || fullUserData.Team || null
    } catch (e) {
      console.error('Error parsing fullUserData from localStorage:', e)
    }
  }
  
  // Also check cookies as another backup
  if (!teamData) {
    try {
      const cookieData = document.cookie
        .split('; ')
        .find(row => row.startsWith('userData='))
        ?.split('=')[1]
      
      if (cookieData) {
        const userData = JSON.parse(decodeURIComponent(cookieData))
        teamData = userData.team || userData.Team || null
      }
    } catch (e) {
      console.error('Error parsing userData from cookies:', e)
    }
  }
  
  return teamData
}

const getUserRole = () => {
  return authStore?.userData?.role || 
         authStore?.userData?.roles?.[0]?.name ||
         authStore?.userData?.roles?.[0] ||
         null
}

const userTeam = ref(getUserTeamData())
const userRole = ref(getUserRole())


// Modal control
const showParcelModal = ref(false)
const selectedParcel = ref(null)

let parcelsChannel = null

const handleParcelCreated = data => {
  // Skip notifications during bulk import
  if (window.__bulkImportInProgress) {
    return
  }

  // Parcel created event handler
  
  // Check if user should see this notification based on team access
  if (!shouldShowNotificationForParcel(data)) {
    return
  }
  
  toast.success(`New parcel ${data.parcel?.barcode || 'unknown'} has been created`, {
    timeout: 5000,
    closeButton: undefined,
  })

  // Note: Server-side notifications will be handled automatically via the notification system
  // We only show the toast here for immediate user feedback
}

const handleParcelUpdated = data => {
  // Skip notifications during bulk import
  if (window.__bulkImportInProgress) {
    return
  }

  // Check if user should see this notification based on team access
  if (!shouldShowNotificationForParcel(data)) {
    return
  }
  
  toast.info(`Parcel ${data.parcel?.barcode || 'unknown'} has been updated`, {
    timeout: 5000,
    closeButton: undefined,
  })

  // Note: Server-side notifications will be handled automatically via the notification system
  // We only show the toast here for immediate user feedback
}

const handleParcelStatusUpdated = data => {
  // Check if user should see this notification based on team access
  if (!shouldShowNotificationForParcel(data)) {
    return
  }
  
  // Map status names for display purposes
  // Defensively read both shapes — top-level (new) and nested in parcel
  // (older payloads). Fixes the "status changed to unknown" toast.
  let displayStatus = data.status || data.parcel?.status || 'unknown';
  if (displayStatus === 'in_transit') displayStatus = 'packaged';
  if (displayStatus === 'ready_for_pickup') displayStatus = 'loaded';
  
  toast.info(`Parcel ${data.parcel?.barcode || 'unknown'} status changed to ${displayStatus}`, {
    timeout: 5000,
    closeButton: undefined,
  })

  // Note: Server-side notifications will be handled automatically via the notification system
  // We only show the toast here for immediate user feedback
}

/**
 * Check if the user should see notifications for a specific parcel based on team access
 */
const shouldShowNotificationForParcel = (data) => {
  // Admin users can see all notifications
  if (userRole.value === 'admin' || userRole.value === 'mobile-admin') {
    return true
  }

  // Check if parcel has team information
  const parcelTeamId = data.parcel?.team_id || data.team_id
  const parcelTeamCode = data.parcel?.team?.code || data.team_code
  
  if (!parcelTeamId && !parcelTeamCode) {
    // If parcel has no team info, show to all users (fallback)
    console.warn('Parcel notification without team info:', data.parcel?.barcode)
    return true
  }

  // TEMPORARY: If user has no team data, show all notifications (for debugging)
  if (!userTeam.value) {
    console.warn('⚠️ User has no team data - showing all notifications. This is temporary for debugging.', {
      userTeam: userTeam.value,
      userRole: userRole.value,
      parcelTeamId,
      parcelBarcode: data.parcel?.barcode
    })
    return true
  }

  // Check if user belongs to the same team
  if (userTeam.value) {
    // Check by team ID first (more reliable)
    if (parcelTeamId && userTeam.value.id === parcelTeamId) {
      return true
    }
    
    // Fallback to team code comparison
    if (parcelTeamCode && userTeam.value.code === parcelTeamCode) {
      return true
    }
  }
  
  // Log for debugging purposes in development
  if (process.env.NODE_ENV !== 'production') {
    console.log('Notification filtered out:', {
      userTeam: userTeam.value,
      parcelTeamId,
      parcelTeamCode,
      parcelBarcode: data.parcel?.barcode
    })
  }
  
  return false
}

const retryFetchParcel = async (id, maxAttempts = 3, delay = 500) => {
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      const response = await axios.get(`/shipment/incoming-parcels/${id}`)
      return response.data
    } catch (err) {
      if (attempt === maxAttempts) throw err
      await new Promise(resolve => setTimeout(resolve, delay))
    }
  }
}

const handleNotificationClick = async notification => {
  if (notification.type === 'parcel' && notification.parcelId) {
    try {
      selectedParcel.value = await retryFetchParcel(notification.parcelId)
      showParcelModal.value = true
    } catch (error) {
      console.error('Error fetching parcel:', error)
      toast.error('Could not load parcel details')
    }
  }
}

const addGlobalEventListeners = () => {
  window.addEventListener('notification:click', event => {
    if (event.detail?.type === 'parcel') {
      handleNotificationClick(event.detail)
    }
  })

  window.addEventListener('notifications:markAllRead', async () => {
    await markAllRead()
    window.dispatchEvent(new CustomEvent('refresh:notifications'))
  })
}

onMounted(() => {
  // Initialize listeners
  subscribeToParcelChannel()
  addGlobalEventListeners()
  fetchNotifications()
})

const subscribeToParcelChannel = () => {
  if (!window.Echo) {
    console.error('Echo not initialized')
    
    // Schedule a retry after 2 seconds
    setTimeout(() => {
      if (window.Echo) {
        subscribeToParcelChannel()
      }
    }, 2000)
    
    return
  }

  try {
    parcelsChannel = window.Echo.channel('parcels')

    // Listen for both namespaced and simplified event names
    parcelsChannel.listen('Modules\\Shipment\\Events\\IncomingParcelCreated', event => {
      handleParcelCreated({ parcel: event.parcel })
    })
    
    // Also listen with the broadcastAs name
    parcelsChannel.listen('IncomingParcelCreated', event => {
      handleParcelCreated({ parcel: event.parcel })
    })

    parcelsChannel.listen('Modules\\Shipment\\Events\\ParcelCreated', event => {
      handleParcelCreated({ parcel: event.parcel })
    })
    
    parcelsChannel.listen('ParcelCreated', event => {
      handleParcelCreated({ parcel: event.parcel })
    })

    parcelsChannel.listen('Modules\\Shipment\\Events\\ParcelUpdated', event => {
      handleParcelUpdated({ parcel: event.parcel })
    })
    
    parcelsChannel.listen('ParcelUpdated', event => {
      handleParcelUpdated({ parcel: event.parcel })
    })

    parcelsChannel.listen('Modules\\Shipment\\Events\\ParcelStatusUpdated', event => {
      // Pass status from either top-level (new payload) or nested in parcel
      // (older). Backend canonical key is top-level event.status.
      handleParcelStatusUpdated({
        parcel: event.parcel,
        status: event.status ?? event.parcel?.status,
      })
    })

    parcelsChannel.listen('ParcelStatusUpdated', event => {
      handleParcelStatusUpdated({
        parcel: event.parcel,
        status: event.status ?? event.parcel?.status,
      })
    })
    
    // Add connection status logging in development
    if (process.env.NODE_ENV !== 'production') {
      window.Echo.connector.pusher.connection.bind('connected', () => {
        console.log('✅ WebSocket connected successfully')
      })
      
      window.Echo.connector.pusher.connection.bind('error', error => {
        console.error('❌ WebSocket connection error:', error)
      })
    }
    
  } catch (error) {
    console.error('Error subscribing to parcels channel:', error)
    
    // Schedule a retry after 5 seconds
    setTimeout(() => {
      console.log('Retrying channel subscription...')
      subscribeToParcelChannel()
    }, 5000)
  }
}

onBeforeUnmount(() => {
  if (window.Echo && parcelsChannel) {
    window.Echo.leaveChannel('parcels')
    parcelsChannel = null
  }

  window.removeEventListener('notification:click', handleNotificationClick)
  window.removeEventListener('notifications:markAllRead', markAllRead)
})
</script>
